import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton, Modal } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Fulfillment } from 'api/models/Fulfillment';
import { ApiService } from 'api/services/ApiService';
import { AttioQueryKeys } from 'common/constants/analyst-dashboards';
import { useEventBus } from 'context/EventBusContext/EventBusContext';
import { EventType } from 'context/EventBusContext/EventBusContext.type';
import { useThemeMode } from 'context/ThemeModeContext';
import { useAttioValuationContext } from 'dashboard409a/context/AttioValuationContext';
import { useCompanyMeasurementDateByAttioValuationId } from 'dashboard409a/hooks/useCompanyMeasurementDateByAttioValuationId';
import { useUndoableStatusChange } from 'dashboard409a/hooks/useUndoableStatusChange';
import {
  EmptyOperationsMessage,
  ModelStatus,
  ValuationDealInfo,
  ValuedEntity,
  ValuedInformation,
} from 'dashboard409a/pages/Operations/components';
import { modelStatusMap } from 'dashboard409a/pages/Operations/components/ModelStatus/ModelStatus.constants';
import { NotesDisplay } from 'dashboard409a/pages/Operations/components/NotesDisplay';
import { ModelStatus as ModelStatusEnum } from 'dashboard409a/pages/Workboard/common/types';
import { sendFinalInvoice, updateValuationDealStage } from 'dashboard409a/services/attio-common';
import { AttioValuationFormattedResponse } from 'dashboard409a/services/attio.types';
import { FulfillmentQueryKeys, useFulfillmentList } from 'dashboard409a/services/fulfillment';
import type { AttioValuationDetailResponse } from 'dashboard409a/services/shared-types';
import { buildEnsureFulfillmentBaseRequest } from 'dashboard409a/utilities/ensureFulfillmentFromValuationDetail';
import {
  runValuationStatusTransition,
} from 'dashboard409a/utilities/ensureValuationStatusChange';
import lightTheme, { darkTheme } from 'theme';
import { Logger } from 'utilities';
import { DROPBOX_ROOT_OPTIONS } from './dropbox.constants';
import { normalizeFolderSegment } from './dropbox.utils';
import { BentoGrid, CloseButtonContainer, StyledModalBox } from './OperationsModal.styles';
import { OperationsModalProps, StatusRollbackSnapshot } from './OperationsModal.types';
import useDropboxFolderSync from './useDropboxFolderSync';
import { addBusinessDays, useValuationDetail } from './useOperationsModal';
import { getValuationFolderNamePrefix } from './valuationTypeFolderPrefix.constants';

const OperationsModal: FC<OperationsModalProps> = ({
  open,
  onClose,
  data,
  isFromModels = false,
}) => {
  const { resolvedMode } = useThemeMode();
  const activeTheme = resolvedMode === 'dark' ? darkTheme : lightTheme;
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { publish } = useEventBus();
  const { setCurrentAttioCompanyName, setCurrentAttioValuation } = useAttioValuationContext();
  const [optimisticStatus, setOptimisticStatus] = useState<{ statusId: string; label: string } | null>(null);
  const pendingPersistSnapshotRef = useRef<StatusRollbackSnapshot | null>(null);
  const undoFailureSnapshotRef = useRef<StatusRollbackSnapshot | null>(null);

  // Use the custom hook to handle valuation detail transformation and API calls
  const {
    valuationDetail,
    valuationId,
    isLoading,
    isError,
    error,
    refetchValuationDetail,
    statusOptions,
    isLoadingStatusOptions,
  } = useValuationDetail({ data, open });

  // Fetch company measurement date by attio_valuation_id - this includes the full company data
  const {
    data: companyMeasurementDateByAttioId,
    error: companyMeasurementDateError,
    isLoading: isLoadingCompanyMeasurementDate,
  } = useCompanyMeasurementDateByAttioValuationId(valuationId);

  // Extract company from companyMeasurementDateByAttioId
  const currentCompany = companyMeasurementDateByAttioId?.company;

  // Use the App company ID for fulfillment operations (not the Attio valued entity ID)
  // The fulfillment API expects the app company ID from companyMeasurementDateByAttioId
  const appCompanyId = useMemo(() => {
    if (currentCompany?.id) {
      return currentCompany.id.toString();
    }
    return undefined;
  }, [currentCompany?.id]);

  // Fetch fulfillment data to check if model support exists
  const { data: fulfillmentData, refetch: refetchFulfillments } = useFulfillmentList(appCompanyId);

  // Find existing fulfillment for the current company measurement date
  const existingFulfillment = useMemo(() => {
    if (!fulfillmentData?.results || !companyMeasurementDateByAttioId?.id) {
      return null;
    }
    return (
      fulfillmentData.results.find(
        fulfillment => fulfillment.company_measurement_date === companyMeasurementDateByAttioId.id
      ) || null
    );
  }, [fulfillmentData, companyMeasurementDateByAttioId?.id]);

  const [isEnsureModelSupportPending, setIsEnsureModelSupportPending] = useState(false);

  const dismissUndoRef = useRef<() => void>(() => {});

  const suggestedCompanyFolderName = useMemo(
    () => normalizeFolderSegment(valuationDetail?.valuedEntity?.companyName || ''),
    [valuationDetail?.valuedEntity?.companyName]
  );

  const suggestedValuationFolderName = useMemo(() => {
    const rawDate = valuationDetail?.valuedInformation?.valuationDate || valuationDetail?.deal?.valuationDate;
    if (!rawDate) {
      return '';
    }
    const parsed = new Date(rawDate);
    if (Number.isNaN(parsed.getTime())) {
      return '';
    }
    const year = parsed.getFullYear();
    const month = `${parsed.getMonth() + 1}`.padStart(2, '0');
    const datePart = `${year}.${month}`;
    const prefix = getValuationFolderNamePrefix(valuationDetail?.valuedInformation?.valuationType);
    return prefix ? `${prefix} ${datePart}` : datePart;
  }, [
    valuationDetail?.valuedInformation?.valuationDate,
    valuationDetail?.valuedInformation?.valuationType,
    valuationDetail?.deal?.valuationDate,
  ]);

  const {
    dropboxConnected,
    connectDropbox,
    dropboxRoot,
    setDropboxRoot,
    companyFolderStatus,
    valuationFolderStatus,
    companyFolderSuggestions,
    valuationFolderSuggestions,
    companyFolderMatchPaths,
    valuationFolderMatchPaths,
    companyFolderSimilarMatchesCount,
    valuationFolderSimilarMatchesCount,
    handleSearchCompanyFolder,
    handleSearchValuationFolder,
    handleCreateCompanyFolder,
    handleCreateValuationFolder,
    handleUpdateCompanyFolder,
    handleUpdateValuationFolder,
  } = useDropboxFolderSync({
    open,
    valuationId,
    currentCompany,
    companyMeasurementDateById: companyMeasurementDateByAttioId,
    isLoadingMeasurementDate: isLoadingCompanyMeasurementDate,
    measurementDateError: companyMeasurementDateError,
    suggestedCompanyFolderName,
    suggestedValuationFolderName,
    queryClient,
  });

  const applyOptimisticStatusDisplay = useCallback(
    (statusId: string) => {
      if (!valuationId) {
        return;
      }
      const selectedStatus = statusOptions.find(option => option.id === statusId);
      const label = selectedStatus?.label || 'status';
      setOptimisticStatus({ statusId, label });
      queryClient.setQueryData(
        [AttioQueryKeys.VALUATION_ITEM, valuationId],
        (old: AttioValuationDetailResponse | undefined) => (old ? { ...old, statusId, status: label } : old)
      );
      publish(EventType.WORKBOARD_VALUATION_UPDATED, {
        valuationId,
        updates: { status: label },
      });
    },
    [valuationId, statusOptions, queryClient, publish]
  );

  const captureStatusRollbackSnapshot = useCallback((): StatusRollbackSnapshot => {
    const queryData = valuationId ? queryClient.getQueryData([AttioQueryKeys.VALUATION_ITEM, valuationId]) : undefined;
    return {
      queryData,
      optimisticStatus,
      workboardStatusLabel: optimisticStatus?.label ?? valuationDetail?.status,
    };
  }, [valuationId, queryClient, optimisticStatus, valuationDetail?.status]);

  const restoreStatusRollbackSnapshot = useCallback(
    (snapshot: StatusRollbackSnapshot) => {
      if (valuationId) {
        const queryKey = [AttioQueryKeys.VALUATION_ITEM, valuationId];
        if (snapshot.queryData !== undefined) {
          queryClient.setQueryData(queryKey, snapshot.queryData);
        }
      }
      setOptimisticStatus(snapshot.optimisticStatus);
      if (valuationId && snapshot.workboardStatusLabel !== undefined) {
        publish(EventType.WORKBOARD_VALUATION_UPDATED, {
          valuationId,
          updates: { status: snapshot.workboardStatusLabel },
        });
      }
    },
    [valuationId, queryClient, publish]
  );

  const persistStatusChange = useCallback(
    async (statusId: string, snapshot: StatusRollbackSnapshot) => {
      if (!valuationId) {
        return;
      }
      try {
        if (!valuationDetail) {
          restoreStatusRollbackSnapshot(snapshot);
          dismissUndoRef.current();
          enqueueSnackbar('Valuation details are not loaded yet. Please wait and try again.', {
            variant: 'error',
          });
          return;
        }

        const selectedStatus = statusOptions.find(option => option.id === statusId);
        const statusLabel = selectedStatus?.label || 'status';

        const transitionResult = await runValuationStatusTransition({
          valuationId,
          statusId,
          statusLabel,
          queryClient,
          modalData: data,
          fulfillmentContext: {
            valuationDetail,
            existingFulfillment,
            companyMeasurementDateId: companyMeasurementDateByAttioId?.id,
            appCompanyId,
          },
        });
        if (!transitionResult.ok) {
          restoreStatusRollbackSnapshot(snapshot);
          dismissUndoRef.current();

          if (transitionResult.reason === 'missing_valuation_date') {
            enqueueSnackbar(
              'Valuation date is missing or invalid. Cannot sync fulfillment. Please fix the valuation in Attio.',
              { variant: 'error', autoHideDuration: 10000 }
            );
          } else if (transitionResult.reason === 'ensure_failed' || transitionResult.reason === 'load_failed') {
            enqueueSnackbar(
              'Failed to sync fulfillment. Status was not updated. Please try again or contact support.',
              { variant: 'error' }
            );
          } else {
            enqueueSnackbar('Failed to update status. Please try again or contact support.', { variant: 'error' });
          }
          return;
        }

        await refetchFulfillments();

        publish(EventType.WORKBOARD_VALUATION_UPDATED, {
          valuationId,
          updates: { status: statusLabel },
        });
        setOptimisticStatus(null);
      } catch (statusError) {
        Logger.error('Error updating valuation status:', statusError);
        restoreStatusRollbackSnapshot(snapshot);
        dismissUndoRef.current();
        enqueueSnackbar('Failed to update status. Please try again or contact support.', { variant: 'error' });
      }
    },
    [
      valuationId,
      valuationDetail,
      data,
      statusOptions,
      existingFulfillment,
      companyMeasurementDateByAttioId?.id,
      appCompanyId,
      queryClient,
      publish,
      refetchFulfillments,
      enqueueSnackbar,
      restoreStatusRollbackSnapshot,
    ]
  );

  const onCommitStatus = useCallback(
    async (statusId: string) => {
      const snap = pendingPersistSnapshotRef.current;
      if (!snap || !valuationId) {
        return;
      }
      await persistStatusChange(statusId, snap);
    },
    [valuationId, persistStatusChange]
  );

  const onUndoStatus = useCallback(
    async (previousStatusId: string) => {
      const snap = undoFailureSnapshotRef.current;
      if (!snap || !valuationId) {
        return;
      }
      await persistStatusChange(previousStatusId, snap);
    },
    [valuationId, persistStatusChange]
  );

  const onBeforeUndo = useCallback(
    (previousStatusId: string) => {
      undoFailureSnapshotRef.current = captureStatusRollbackSnapshot();
      applyOptimisticStatusDisplay(previousStatusId);
    },
    [captureStatusRollbackSnapshot, applyOptimisticStatusDisplay]
  );

  const { triggerChange, handleUndo, dismissUndo, isUndoPending, secondsLeft } = useUndoableStatusChange({
    valuationId,
    onCommit: onCommitStatus,
    onUndo: onUndoStatus,
    onBeforeUndo,
  });

  dismissUndoRef.current = dismissUndo;

  useEffect(() => {
    if (valuationDetail) {
      setCurrentAttioValuation(valuationDetail as unknown as AttioValuationFormattedResponse);
      if (valuationDetail?.valuedEntity.companyName) {
        setCurrentAttioCompanyName(valuationDetail?.valuedEntity.companyName);
      }
    }
  }, [valuationDetail, setCurrentAttioCompanyName, setCurrentAttioValuation]);

  useEffect(() => {
    if (!open) {
      setOptimisticStatus(null);
      dismissUndoRef.current();
    }
  }, [open]);

  if (isError) {
    Logger.error('Error fetching Attio valuations:', error);
  }

  const handleStatusChange = async (statusId: string) => {
    if (!valuationId) {
      return;
    }
    const effectiveCurrentStatusId = optimisticStatus?.statusId ?? valuationDetail?.statusId;
    if (effectiveCurrentStatusId === statusId) {
      return;
    }

    const previousStatusId = effectiveCurrentStatusId;
    const snapshot = captureStatusRollbackSnapshot();
    applyOptimisticStatusDisplay(statusId);
    enqueueSnackbar('Valuation status has been updated', { variant: 'success' });
    pendingPersistSnapshotRef.current = snapshot;

    if (!previousStatusId) {
      onCommitStatus(statusId).catch(() => {});
      return;
    }

    triggerChange(statusId, previousStatusId).catch(() => {});
  };

  const handleDealStageChange = async (dealStageId: string) => {
    if (!dealStageId || !valuationDetail?.deal?.dealId) {
      return;
    }

    try {
      const { dealId } = valuationDetail.deal;
      await updateValuationDealStage(dealId, dealStageId);

      // Publish event to update the table row immediately
      publish(EventType.WORKBOARD_VALUATION_UPDATED, {
        valuationId,
        updates: {
          dealStage: dealStageId,
        },
      });

      // Invalidate all relevant caches
      await Promise.all([
        // Invalidate the valuation detail query
        queryClient.invalidateQueries({
          queryKey: [AttioQueryKeys.VALUATION_ITEM, valuationId],
          exact: false,
        }),
        // Invalidate the valuations list (for table refresh)
        queryClient.invalidateQueries({
          queryKey: ['valuations-all'],
          exact: false,
        }),
        // Invalidate current/past valuations
        queryClient.invalidateQueries({
          queryKey: [AttioQueryKeys.CURRENT_VALUATIONS],
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: [AttioQueryKeys.PAST_VALUATIONS],
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: [AttioQueryKeys.VALUATIONS],
          exact: false,
        }),
      ]);

      // Manually refetch the valuation detail to update the modal
      await refetchValuationDetail();

      enqueueSnackbar('Deal stage has been updated', { variant: 'success' });
    } catch (dealStageError) {
      Logger.error('Error updating deal stage:', dealStageError);
      enqueueSnackbar('Failed to update deal stage. Please try again.', { variant: 'error' });
    }
  };

  const handleSendFinalInvoice = async () => {
    await sendFinalInvoice(valuationDetail?.deal?.dealId as string);

    enqueueSnackbar('Final invoice has been sent', { variant: 'success' });
  };

  // Handle model support creation (ensure graph + fulfillment when CMD was missing)
  const handleCreateModelSupport = async () => {
    if (!valuationId || !valuationDetail) {
      enqueueSnackbar('Valuation details are not loaded yet. Please wait and try again.', {
        variant: 'error',
      });
      return;
    }

    const ensureBase = buildEnsureFulfillmentBaseRequest({
      valuationDetail,
      valuationId,
      modalData: data,
    });
    if (!ensureBase) {
      enqueueSnackbar(
        'Valuation date is missing or invalid. Cannot create model support. Please fix the valuation in Attio.',
        { variant: 'error', autoHideDuration: 10000 }
      );
      return;
    }

    setIsEnsureModelSupportPending(true);
    try {
      await ApiService.apiDashboardOperationsEnsureFulfillmentForAttioValuationCreate({
        ...ensureBase,
        model_status: modelStatusMap[ModelStatusEnum.DRAFT],
        supporting_due_date: addBusinessDays(new Date(), 4),
        reviewers: [],
        preparers: [],
      });

      await queryClient.invalidateQueries({
        queryKey: ['company-measurement-date-by-attio-valuation-id', valuationId],
      });
      await queryClient.invalidateQueries({
        queryKey: [FulfillmentQueryKeys.FULFILLMENT_LIST],
        exact: false,
      });
      await refetchFulfillments();
      enqueueSnackbar('Model support created successfully', { variant: 'success' });
    } catch (fulfillmentError) {
      Logger.error('Failed to create model support:', fulfillmentError);
      enqueueSnackbar('Failed to create model support. Please try again.', { variant: 'error' });
    } finally {
      setIsEnsureModelSupportPending(false);
    }
  };

  const handleModelTeamChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!existingFulfillment || !appCompanyId || !valuationId) {
      enqueueSnackbar('Unable to update model team: Company information not available', {
        variant: 'error',
      });
      return;
    }
    try {
      await ApiService.apiDashboardCompanyOperationsFulfillmentPartialUpdate(appCompanyId, valuationId, {
        id: existingFulfillment.id,
        is_slc_model: event.target.checked,
      } as Fulfillment);
      refetchFulfillments();
    } catch (error_) {
      Logger.error('Error updating model team:', error_);
      enqueueSnackbar('Failed to update model team. Please try again.', { variant: 'error' });
    }
  };

  const combinedIsLoading = isLoading;

  const dealBox = (
    <Box>
      <ValuationDealInfo
        deal={valuationDetail?.deal}
        isLoading={combinedIsLoading}
        onDealStageChange={handleDealStageChange}
        onSendFinalInvoice={handleSendFinalInvoice}
        isFromModels={isFromModels}
      />
    </Box>
  );

  const valuedEntityBox = (
    <Box>
      <ValuedEntity
        valuedEntity={valuationDetail?.valuedEntity}
        isLoading={combinedIsLoading}
        companyFolder={currentCompany?.file_storage_reference || null}
        dropboxRoot={dropboxRoot}
        dropboxRootOptions={DROPBOX_ROOT_OPTIONS}
        onDropboxRootChange={setDropboxRoot}
        companyFolderStatus={companyFolderStatus}
        companyFolderMatchPaths={companyFolderMatchPaths}
        companyFolderSimilarMatchesCount={companyFolderSimilarMatchesCount}
        companyFolderSuggestions={companyFolderSuggestions}
        suggestedCompanyFolderName={suggestedCompanyFolderName}
        dropboxConnected={dropboxConnected}
        onDropboxConnect={connectDropbox}
        onSearchCompanyFolder={handleSearchCompanyFolder}
        onCreateCompanyFolder={handleCreateCompanyFolder}
        onUpdateCompanyFolder={handleUpdateCompanyFolder}
      />
    </Box>
  );

  const modelStatusBox = (
    <Box>
      <ModelStatus
        fulfillmentData={fulfillmentData}
        isLoading={combinedIsLoading}
        onCreateModelSupport={handleCreateModelSupport}
        isCreatingModelSupport={isEnsureModelSupportPending}
        companyId={appCompanyId}
        valuationId={valuationId}
        companyMeasurementDateId={companyMeasurementDateByAttioId?.id}
        isSlcModel={existingFulfillment?.is_slc_model || false}
        onSlcModelChange={handleModelTeamChange}
      />
    </Box>
  );

  const notesBox = (
    <Box>
      <NotesDisplay isFromModels={isFromModels} />
    </Box>
  );

  const valuedInformationFullWidth = (
    <Box sx={{ gridColumn: '1 / -1' }}>
      <ValuedInformation
        isFromModels={isFromModels}
        valuedInformation={valuationDetail?.valuedInformation}
        isLoading={combinedIsLoading}
        statusOptions={statusOptions}
        currentStatusId={optimisticStatus?.statusId ?? valuationDetail?.statusId}
        onStatusChange={handleStatusChange}
        isStatusLoading={combinedIsLoading || isLoadingStatusOptions}
        companyName={currentCompany?.name || valuationDetail?.valuedEntity?.companyName || null}
        companyFolder={currentCompany?.file_storage_reference || null}
        valuationFolder={companyMeasurementDateByAttioId?.file_storage_reference || null}
        dropboxRoot={dropboxRoot}
        dropboxRootOptions={DROPBOX_ROOT_OPTIONS}
        onDropboxRootChange={setDropboxRoot}
        valuationFolderStatus={valuationFolderStatus}
        valuationFolderMatchPaths={valuationFolderMatchPaths}
        valuationFolderSimilarMatchesCount={valuationFolderSimilarMatchesCount}
        valuationFolderSuggestions={valuationFolderSuggestions}
        suggestedValuationFolderName={suggestedValuationFolderName}
        dropboxConnected={dropboxConnected}
        onDropboxConnect={connectDropbox}
        onSearchValuationFolder={handleSearchValuationFolder}
        onCreateValuationFolder={handleCreateValuationFolder}
        onUpdateValuationFolder={handleUpdateValuationFolder}
        undoProps={{ isUndoPending, secondsLeft, onUndo: handleUndo }}
      />
    </Box>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        '&:focus': {
          outline: 'none',
        },
        '&:focus-visible': {
          outline: 'none',
        },
      }}>
      <ThemeProvider theme={activeTheme}>
        <StyledModalBox onClick={e => e.stopPropagation()}>
          <CloseButtonContainer>
            <IconButton onClick={onClose} size="small" aria-label="close" sx={{ padding: '4px' }}>
              <CloseIcon sx={{ fontSize: '18px' }} />
            </IconButton>
          </CloseButtonContainer>
          {valuationId ? (
            <BentoGrid>
              {isFromModels ? (
                <>
                  {dealBox}
                  {valuedEntityBox}
                  {modelStatusBox}
                  {notesBox}
                  {valuedInformationFullWidth}
                </>
              ) : (
                <>
                  {valuedInformationFullWidth}
                  {dealBox}
                  {valuedEntityBox}
                  {modelStatusBox}
                  {notesBox}
                </>
              )}
            </BentoGrid>
          ) : (
            <EmptyOperationsMessage />
          )}
        </StyledModalBox>
      </ThemeProvider>
    </Modal>
  );
};

export default OperationsModal;
